import { Button, InputText } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { getContactNumber } from "@sellar-io/common";
import { ReactElement, useEffect } from "react";
import { useForm } from "react-hook-form";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderUpdate } from "../../../hooks/api/v2/order/v2/useOrderUpdate";
import { useSupplierRelationship } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationship";
import { useSupplierRelationshipUpdate } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipUpdate";
import { Alert } from "../../alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../modal/Dialog";
import { SpinnerLegacy } from "../..";

export function OrderUpdateEmailAddressModal({
  onDismiss,
  active,
  connectionId,
  order,
}: {
  onDismiss: () => void;
  active: boolean;
  connectionId: number;
  order: OrderGetResponseType;
}): ReactElement {
  const connection = useSupplierRelationship(connectionId);

  const form = useForm({
    shouldUseNativeValidation: true,
    reValidateMode: "onChange",
  });

  const retailerContactSms = getContactNumber(
    order,
    connection.data?.retailerContactSmsNumber,
  );

  useEffect(() => {
    form.setValue(
      "email",
      connection.data?.buyerContactEmail ?? order.Retailer.Business.email,
    );
    form.setValue("phoneNumber", retailerContactSms);
  }, [
    connection.data,
    form,
    order.Retailer.Business.email,
    retailerContactSms,
  ]);

  const supplierRelationshipUpdateMutation =
    useSupplierRelationshipUpdate(connectionId);
  const orderUpdateMutation = useOrderUpdate(order.id);

  const connectionQueryLoading =
    connection.isLoading && connection.fetchStatus !== "idle";
  const isLoading =
    connectionQueryLoading ||
    supplierRelationshipUpdateMutation.isPending ||
    orderUpdateMutation.isPending;
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Edit customer details"}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        <>
          {isLoading ? (
            <SpinnerLegacy />
          ) : (
            <>
              {supplierRelationshipUpdateMutation.isError && (
                <Alert
                  type={"error"}
                  message={
                    apiV2AssertError(supplierRelationshipUpdateMutation.error)
                      ?.response?.data.message ?? "An unknown error occurred"
                  }
                />
              )}
              {orderUpdateMutation.isError && (
                <Alert
                  type={"error"}
                  message={
                    apiV2AssertError(orderUpdateMutation.error)?.response?.data
                      .message ?? "An unknown error occurred"
                  }
                />
              )}
              <form
                onSubmit={form.handleSubmit((data) => {
                  supplierRelationshipUpdateMutation.mutate(
                    {
                      buyerContactEmail: data.email,
                      retailerContactSmsNumber: data.phoneNumber,
                    },
                    {
                      onSuccess: () => {
                        orderUpdateMutation.mutate(
                          {
                            requestingBusinessType: "supplier",
                            retailerContactSmsNumber: data.phoneNumber,
                          },
                          {
                            onSuccess: () => onDismiss(),
                          },
                        );
                      },
                    },
                  );
                })}
                className={"flex flex-col gap-4"}
              >
                <InputLabel>Contact Email</InputLabel>
                <InputContainer>
                  <InputText {...form.register("email", { required: false })} />
                </InputContainer>
                <InputLabel>Contact Phone number</InputLabel>
                <InputContainer>
                  <InputText
                    {...form.register("phoneNumber", { required: false })}
                  />
                </InputContainer>
                <DialogCTA>
                  <Button type="submit">Update</Button>
                </DialogCTA>
              </form>
            </>
          )}
        </>
      </DialogContent>
    </Dialog>
  );
}
